import type {SelectItem} from '@jetbrains/ring-ui/components/select/select'
import {useCallback, useEffect, useMemo, useState} from 'react'

import {connectionId} from '../../../../../CreatePipelinePage/CreatePipelinePage.consts'
import {repositoryBranchSpecification} from '../../../../../CreatePipelinePage/Repositories/RepositoryList/useOutsideRepository.consts'
import {pipelinesApi} from '../../../../../services/pipelinesApi'
import type {PipelineBaseRepository, RepositorySelectItem} from '../PipelineRepositories.types'
import {getRepositoryName} from '../PipelineRepositories.utils'

import {getBranchSpecification} from './PipelineRepositoryForm.utils'

const fetchBranchesArgs = {
  branchSpecification: repositoryBranchSpecification,
  vcsName: 'jetbrains.git',
}

function usePipelineRepositoryForm(
  repositoryUrl: string,
  branch: string,
  included: string,
  excluded: string,
  onConfirm: (value: PipelineBaseRepository) => void,
) {
  const repositoryName = getRepositoryName(repositoryUrl)
  const [selectedRepository, setSelectedRepository] = useState<string>(repositoryUrl)
  const [autocompleteSelectedValue, setAutocompleteSelectedValue] = useState<
    (SelectItem & {isPrivate?: boolean}) | undefined | null
  >({
    key: repositoryUrl,
    label: repositoryName,
  })
  const [repositoryBranchValue, setRepositoryBranchValue] = useState<SelectItem | undefined | null>(
    {
      key: branch,
      label: branch,
    },
  )
  const [includedDraft, setIncludedDraft] = useState(included)
  const [excludedDraft, setExcludedDraft] = useState(excluded)
  const [fetchBranches, {branches}] = pipelinesApi.endpoints.getRepositoryBranches.useLazyQuery({
    selectFromResult: ({data = []}) => ({branches: data}),
  })
  const branchOptions = useMemo(() => {
    const branchOptionsAccumulator: SelectItem[] = []

    if (selectedRepository) {
      return branches.reduce<SelectItem[]>((accumulator, name) => {
        if (name) {
          accumulator.push({key: name, label: name})
        }

        return accumulator
      }, branchOptionsAccumulator)
    }
    return branchOptionsAccumulator
  }, [branches, selectedRepository])
  const confirmHandler = () => {
    onConfirm({
      branchSpecification: getBranchSpecification(includedDraft, excludedDraft),
      url: selectedRepository,
      branch: `${repositoryBranchValue?.label}`,
      isPrivate: autocompleteSelectedValue?.isPrivate,
      connectionId,
    })
  }
  const branchAutocompleteChangeHandler = useCallback((value?: string) => {
    setRepositoryBranchValue(value ? {key: value, label: value} : null)
  }, [])
  const repositoryAutocompleteChangeHandler = useCallback(
    (selectedItem?: RepositorySelectItem | null) => {
      const {
        key = '',
        isPrivate,
        login,
        name,
        htmlUrl = '',
        defaultBranch,
        label,
      } = selectedItem ?? {}
      const isTypingEvent = typeof key === 'number'
      const nameNew = isTypingEvent ? label?.toString() ?? '' : name
      const selectedNew = login ? `${login}/${name}` : nameNew

      setAutocompleteSelectedValue({key, isPrivate, label: selectedNew})
      setSelectedRepository(htmlUrl)
      if (repositoryBranchValue?.label !== defaultBranch) {
        setRepositoryBranchValue(defaultBranch ? {label: defaultBranch, key: defaultBranch} : null)
      }
    },
    [repositoryBranchValue?.label],
  )
  const isInvalidForm =
    !autocompleteSelectedValue?.key ||
    typeof autocompleteSelectedValue?.key !== 'string' ||
    repositoryBranchValue == null
  const isExtendForm = !!selectedRepository

  useEffect(() => {
    const {key = '', isPrivate} = autocompleteSelectedValue ?? {}
    const isTypingEvent = typeof key === 'number'

    if (key && !isTypingEvent && !isPrivate) {
      fetchBranches({
        ...fetchBranchesArgs,
        connectionId,
        isPrivate: true,
        url: key?.toString(),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autocompleteSelectedValue?.key])

  return {
    isExtendForm,
    includedDraft,
    excludedDraft,
    selectedRepository,
    autocompleteSelectedValue,
    repositoryBranchValue,
    isInvalidForm,
    branchOptions,
    setIncludedDraft,
    setExcludedDraft,
    confirmHandler,
    repositoryAutocompleteChangeHandler,
    branchAutocompleteChangeHandler,
  }
}

export default usePipelineRepositoryForm
